import {
  AvailableAmountResponse,
  AvailableIconAssetsResponse,
  AvailableQuantityResponse,
  CoinName,
  ExchangeToSymbolsMapping,
  ApiGeneralDataResponse,
  ApiResponse,
  GetAvailableQuantityOrAmountRequest,
  PriceGraphData,
  PriceGraphDataRequest,
  PrecisionsData,
  SymbolsMetadata,
  ApiTags,
  GroupData,
  StoreNpsResultRequest,
  ExchangesMetadata,
  GetJobResultResponse,
} from "@shared/types";
import { EndpointBuilder } from "@reduxjs/toolkit/query";

export const commonEndpoints = {
  endpoints: (build: EndpointBuilder<any, any, any>) => ({
    getAvailableIconAssets: build.query<CoinName[], void>({
      query: () => ({
        url: "get_available_icon_assets/",
        method: "GET",
      }),
      transformResponse: (
        response: ApiResponse<AvailableIconAssetsResponse>
      ) => {
        return response?.data?.data || [];
      },
    }),
    getAvailableAmount: build.query<
      AvailableAmountResponse,
      GetAvailableQuantityOrAmountRequest
    >({
      query: (args) => ({
        url: "get_available_amount/",
        method: "POST",
        body: { ...args, is_web: true },
      }),
      transformResponse: (response: ApiResponse<AvailableAmountResponse>) => {
        return response?.data;
      },
      keepUnusedDataFor: 0,
    }),
    getAvailableQuantity: build.query<
      AvailableQuantityResponse,
      GetAvailableQuantityOrAmountRequest
    >({
      query: (args) => ({
        url: "get_available_quantity/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: ApiResponse<AvailableQuantityResponse>) => {
        return response?.data;
      },
      keepUnusedDataFor: 0,
    }),
    getPriceGraphData: build.query<
      ApiGeneralDataResponse<PriceGraphData>,
      PriceGraphDataRequest
    >({
      query: (args) => ({
        url: "/get_price_graph_data/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<PriceGraphData>>
      ) => response.data,
      providesTags: () => [{ type: ApiTags.PRICE_CHARTS, id: "LIST" }],
    }),
    getExchangeToSymbols: build.query<ExchangeToSymbolsMapping, void>({
      query: (args) => ({
        url: "get_exchange_to_symbols/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<ExchangeToSymbolsMapping>>
      ) => {
        return response?.data.data;
      },
    }),
    getSymbolsMetadata: build.query<SymbolsMetadata, void>({
      query: (args) => ({
        url: "get_symbols_metadata/",
        method: "GET",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<SymbolsMetadata>>
      ) => {
        return response?.data.data;
      },
    }),
    getExchangesMetadata: build.query<ExchangesMetadata, void>({
      query: (args) => ({
        url: "get_exchanges_metadata/",
        method: "GET",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<ExchangesMetadata>>
      ) => {
        return response?.data.data;
      },
    }),
    getPrecisions: build.query<PrecisionsData, void>({
      query: (args) => ({
        url: "get_precisions/",
        method: "GET",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<{ precision: PrecisionsData }>
      ) => {
        return response?.data.precision;
      },
    }),
    getGroupData: build.query<GroupData, { sub_group_id: number }>({
      query: (args) => ({
        url: "get_group_data/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<GroupData>>
      ) => response.data?.data,
      providesTags: () => [{ type: ApiTags.GROUP_DATA, id: "LIST" }],
    }),
    getMetadata: build.query<{ bot_link: string }, void>({
      query: () => ({
        url: "get_metadata/",
        method: "POST",
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<{ bot_link: string }>>
      ) => response.data?.data,
    }),
    storeNpsResult: build.mutation<void, StoreNpsResultRequest>({
      query: (args) => ({
        url: "store_nps_result/",
        method: "POST",
        body: args,
      }),
    }),
    acceptTerms: build.mutation<void, void>({
      query: (args) => ({
        url: "accept_terms/",
        method: "POST",
        body: args,
      }),
    }),
    getJobResults: build.query<GetJobResultResponse, { job_id: string }>({
      query: (args) => ({
        url: "get_job_results/",
        method: "POST",
        body: args,
      }),
      keepUnusedDataFor: 0,
    }),
    setAllowSendingPm: build.mutation<void, { allow_sending_pm: boolean }>({
      query: (args) => ({
        url: "set_allow_sending_pm/",
        method: "POST",
        body: args,
      }),
    }),
  }),
};
